import { Button, ProfileEmbedded } from "components";
import { FC, useCallback, useMemo } from "react";

import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from "@chakra-ui/react";
import { ANALYTICS_EVENTS } from "constants/analyticEvents";
import { FEATURES, FEATURE_ACTION } from "constants/features";
import { useCheckPermission, useIsActionAllowed, useTracking } from "hooks";
import { useUpgradeModal } from "hooks/useUpgradeModal";
import { theme } from "@ovou-inc/ovou-ui";
import { tokens } from "@ovou-inc/ovou-ui/tokens";
import { HiOutlineExclamationTriangle } from "react-icons/hi2";
import { isProfileComplete } from "utils/userStatus";
import { IProfileActionCol, USER_TYPE_LABELS } from "./types";

const ProfileActionCol: FC<IProfileActionCol> = ({
  row,
  teamId,
  refetchUsers,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const track = useTracking();
  const canSetupProfile = useCheckPermission(
    FEATURE_ACTION.EDIT,
    FEATURES.MANAGE_USERS_SETUP_PROFILE,
  );
  const canEditProfile = useCheckPermission(
    FEATURE_ACTION.EDIT,
    FEATURES.MANAGE_USERS_EDIT_PROFILE,
  );
  const { openModal } = useUpgradeModal();
  const { handleAction } = useIsActionAllowed();
  const { profile, permission_metadata } = row;
  const isComplete = isProfileComplete(
    profile?.wizard_step,
    profile?.admin_wizard_step,
  );

  const showInfoIcon = useMemo(() => {
    const isMemberViewOnly =
      permission_metadata?.label === USER_TYPE_LABELS.MEMBER_READONLY;
    return isMemberViewOnly && !isComplete;
  }, [permission_metadata?.label, isComplete]);

  const handleSetupProfile = useCallback(() => {
    handleAction(
      canSetupProfile,
      FEATURES.MANAGE_USERS_SETUP_PROFILE,
      () => {
        onOpen();
        track({
          eventName: ANALYTICS_EVENTS.MANAGE_USERS_SETUP_PROFILE,
        });
      },
      openModal,
    );
  }, [canSetupProfile, handleAction, onOpen, openModal, track]);

  const handleEditProfile = useCallback(() => {
    handleAction(
      canEditProfile,
      FEATURES.MANAGE_USERS_EDIT_PROFILE,
      () => {
        onOpen();
        track({
          eventName: ANALYTICS_EVENTS.MANAGE_USERS_EDIT_PROFILE,
        });
      },
      openModal,
    );
  }, [canEditProfile, handleAction, onOpen, openModal, track]);

  const handleCloseEmbedded = () => {
    onClose();
    refetchUsers();
  };

  return (
    <>
      <div className="relative flex justify-center items-center w-full">
        {showInfoIcon && (
          <Popover placement="top" trigger="hover" offset={[0, 16]}>
            <PopoverTrigger>
              <span
                className="absolute flex items-center cursor-pointer"
                style={{ left: "-32px" }}
              >
                <HiOutlineExclamationTriangle
                  color={tokens.color.feedback.error}
                  size={24}
                  strokeWidth={2.5}
                />
              </span>
            </PopoverTrigger>
            <PopoverContent
              maxW="257px"
              px="8px"
              py="6px"
              bg={theme.palette.brand.primary.black}
              color={theme.palette.brand.primary.white}
              border="none"
              borderRadius="8px"
              fontSize="12px"
              fontWeight={400}
              textAlign="center"
            >
              <PopoverArrow bg={theme.palette.brand.primary.black} />
              <PopoverBody p={0}>
                This member is view-only and has no profile. They can’t use OVOU
                until you create one.
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
        {isComplete ? (
          <div className="w-full cursor-pointer" onClick={handleEditProfile}>
            <Button size="small" variant="white" block>
              View Profile
            </Button>
          </div>
        ) : (
          <Button
            onClick={handleSetupProfile}
            size="small"
            variant="black"
            block
          >
            Setup Profile
          </Button>
        )}
      </div>
      <ProfileEmbedded
        isOpen={isOpen}
        onClose={handleCloseEmbedded}
        profileId={String(row?.profile?.id)}
        teamId={String(teamId)}
        roleId={row?.user_role.id}
      />
    </>
  );
};

export default ProfileActionCol;
